import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Grid, Typography, useTheme } from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { DateTime } from "luxon";

import { SolidCard } from "@utilifeed/components";
import {
  convertKwhToMwh,
  PropertiesKpiCards,
  PropertiesMap,
  PropertiesTable,
  summarizeFlexStations,
  usePropertiesKpiData,
  usePropertiesTableData,
  usePropertyUidMaps,
  type PropertyMapEntry,
  type TempAveragesEntry,
  type TempTimeSeriesEntry,
} from "@utilifeed/components/propai";
import { logger as baseLogger } from "@core";
import { useDataApi } from "@core/apis";
import { getInfoBlockPerSubstation, PropAIApi } from "@core/apis/PropAI";
import {
  FlexStation,
  FlexStationParameters,
  getFlexStationParametersOptions,
  getSensorsPerSubstationOptions,
  getSubstationByOrganizationQueryOptions,
  getTemperatureAveragesOptions,
  getTimeSeriesTemperaturesOptions,
  type InfoBlock,
  type InfoBlocksResponse,
} from "@core/apis/shared";
import { extractHeatEnergySum } from "@core/utils/energyUtils";
import { calculateMapCenter } from "@components/GoogleMap/utils";

import organizationStore from "../../stores/organization.store";

type InfoBlockEntry = { data: InfoBlocksResponse; substation_uid: string };
type FlexStationParamsEntry = { flexStationUid: string; targetTemp: number | null };
type InstallAddressBlock = Record<string, unknown> & { substation_uid?: string };
type InstallationBlock = Record<string, unknown> & { secondary_id?: string };
type InfoBlocksEntry = {
  installAddressData: InstallAddressBlock | null;
  installationData: InstallationBlock | null;
  substation_uid: string;
};

function isNotNull<Value>(value: Value | null | undefined): value is Value {
  return value !== null && value !== undefined;
}

const logger = baseLogger.getSubLogger({ name: "DashboardModule" });

const INFO_BLOCKS = ["install_address", "installation"] as const;

export default function DashboardModule() {
  const { t } = useTranslation(["propai_dashboard", "propai", "propai_platform"]);
  const theme = useTheme();
  const { user } = useAuth0();
  const selectedOrganization = organizationStore((state) => state.selectedOrganization);
  const dataApi = useDataApi() as PropAIApi;

  const energyYear = DateTime.now().minus({ years: 1 }).startOf("year").toFormat("yyyy");
  const blockNamesLast12Months = [`core_${energyYear}`];

  const { data: substations, isLoading: loadingSubstations } = useQuery(
    getSubstationByOrganizationQueryOptions(dataApi, selectedOrganization?.uid)
  );

  const { data: infoBlockLast12Months, isLoading: isLoadingEnergyData } = useQueries({
    queries:
      substations?.map((substation) =>
        getInfoBlockPerSubstation(dataApi, substation.uid, blockNamesLast12Months)
      ) || [],
    combine: (results) => ({
      data: results
        .map((result, idx): InfoBlockEntry | null => {
          const data = result.data as InfoBlocksResponse | undefined;
          return result.isSuccess && data && substations?.[idx]
            ? { data, substation_uid: substations[idx].uid }
            : null;
        })
        .filter(isNotNull),
      isLoading: results.some((r) => r.isLoading),
    }),
  });

  const { data: tempTimeSeriesData, isLoading: loadingTimeSeries } = useQueries({
    queries:
      substations?.map((substation) => getTimeSeriesTemperaturesOptions(dataApi, substation.uid)) ||
      [],
    combine: (results) => ({
      data: results
        .map((r) => (r.isSuccess && r.data ? (r.data as TempTimeSeriesEntry) : null))
        .filter(isNotNull),
      isLoading: results.some((r) => r.isLoading),
    }),
  });

  const flexStationsQueries = useQueries({
    queries:
      substations?.map((substation) => getSensorsPerSubstationOptions(dataApi, substation.uid)) ||
      [],
    combine: (results) => ({
      data: results.map((r) => (r.isSuccess ? ((r.data as FlexStation[] | undefined) ?? []) : [])),
      isLoading: results.some((r) => r.isLoading),
    }),
  });

  const flexStationSummaries = useMemo(
    () =>
      (flexStationsQueries.data || []).map((flexStations) => summarizeFlexStations(flexStations)),
    [flexStationsQueries.data]
  );

  const firstFlexStationUids = useMemo(
    () => flexStationSummaries.map((summary) => summary.firstFlexStationUid),
    [flexStationSummaries]
  );

  // Backend-computed averages (current 1h, 24h, 7d) — one flexstation-scoped call per property.
  const { data: temperatureAverages, isLoading: loadingTempAverages } = useQueries({
    queries:
      substations?.map((substation, idx) =>
        getTemperatureAveragesOptions(dataApi, substation.uid, firstFlexStationUids[idx])
      ) || [],
    combine: (results) => ({
      data: results
        .map((r) => (r.isSuccess && r.data ? (r.data as TempAveragesEntry) : null))
        .filter(isNotNull),
      isLoading: results.some((r) => r.isLoading),
    }),
  });

  const { data: flexStationParametersData, isLoading: isLoadingTargetTemp } = useQueries({
    queries:
      flexStationsQueries.data?.flatMap((flexStations: FlexStation[]) =>
        flexStations?.map((flexStation) =>
          getFlexStationParametersOptions(dataApi, flexStation.uid)
        )
      ) || [],
    combine: (results) => ({
      data: results
        .map((r): FlexStationParamsEntry | null => {
          const data = r.data as FlexStationParameters | null | undefined;
          return r.isSuccess && data
            ? {
                flexStationUid: data.flexstation_uid,
                targetTemp: data.indoor_temp?.min ?? null,
              }
            : null;
        })
        .filter(isNotNull),
      isLoading: results.some((r) => r.isLoading),
    }),
  });

  const { data: infoBlocksData } = useQueries({
    queries:
      substations?.map((substation) =>
        getInfoBlockPerSubstation(dataApi, substation.uid, [...INFO_BLOCKS])
      ) || [],
    combine: (results) => ({
      data: results
        .map((result, idx): InfoBlocksEntry | null => {
          const data = result.data as InfoBlocksResponse | undefined;
          if (!(result.isSuccess && data?.collection && substations?.[idx])) return null;
          const blocks = data.collection.reduce<{
            installAddressBlock: InfoBlock | null;
            installationBlock: InfoBlock | null;
          }>(
            (acc, block) => {
              if (block.block_name === "install_address") acc.installAddressBlock = block;
              if (block.block_name === "installation") acc.installationBlock = block;
              return acc;
            },
            { installAddressBlock: null, installationBlock: null }
          );
          if (!blocks.installAddressBlock && !blocks.installationBlock) return null;
          return {
            installAddressData: (blocks.installAddressBlock?.block as InstallAddressBlock) || null,
            installationData: (blocks.installationBlock?.block as InstallationBlock) || null,
            substation_uid: substations[idx].uid,
          };
        })
        .filter(isNotNull),
    }),
  });

  const installAddresses = useMemo(
    () =>
      (infoBlocksData ?? [])
        .map((b) =>
          b?.installAddressData
            ? { ...b.installAddressData, substation_uid: b.substation_uid }
            : null
        )
        .filter(Boolean) as Array<{ substation_uid: string }>,
    [infoBlocksData]
  );

  const secondaryIdByUid = useMemo(() => {
    const map = new Map<string, string>();
    (infoBlocksData ?? []).forEach((b) => {
      if (b?.installationData?.secondary_id) {
        map.set(b.substation_uid, b.installationData.secondary_id);
      }
    });
    return map;
  }, [infoBlocksData]);

  const targetTempsByFlexStation = useMemo(() => {
    const map = new Map<string, number | null>();
    (flexStationParametersData || []).forEach((item) => {
      if (item?.flexStationUid) map.set(item.flexStationUid, item.targetTemp);
    });
    return map;
  }, [flexStationParametersData]);

  const energyMwhByUid = useMemo(() => {
    const map = new Map<string, number>();
    (infoBlockLast12Months || []).forEach((item) => {
      if (!item?.substation_uid) return;
      const sumKwh = extractHeatEnergySum(item.data?.collection || [])?.[0]?.heat_energy_sum || 0;
      map.set(item.substation_uid, convertKwhToMwh(sumKwh));
    });
    return map;
  }, [infoBlockLast12Months]);

  const propertyEntries = useMemo<PropertyMapEntry[]>(() => {
    const tempByUid = new Map<string, TempAveragesEntry>();
    (temperatureAverages || []).forEach((entry) => {
      if (entry?.substation_uid) tempByUid.set(entry.substation_uid, entry);
    });

    return (substations || []).map((substation, idx) => {
      const summary = flexStationSummaries[idx];
      const averages = tempByUid.get(substation.uid);
      const firstFlexStationUid = summary?.firstFlexStationUid;
      return {
        uid: substation.uid,
        summary,
        secondaryId: secondaryIdByUid.get(substation.uid) ?? null,
        targetTemp: firstFlexStationUid
          ? (targetTempsByFlexStation.get(firstFlexStationUid) ?? null)
          : null,
        currentTemp: averages?.current ?? null,
        avgTemp24h: averages?.avgTemp24h ?? null,
        energyMwh: energyMwhByUid.get(substation.uid) ?? 0,
      };
    });
  }, [
    substations,
    flexStationSummaries,
    temperatureAverages,
    secondaryIdByUid,
    targetTempsByFlexStation,
    energyMwhByUid,
  ]);

  const maps = usePropertyUidMaps(propertyEntries);

  const kpiInput = usePropertiesKpiData({
    tempAveragesData: temperatureAverages,
    tempTimeSeriesData,
  });

  const tableRows = usePropertiesTableData({
    substations,
    addressByUid: maps.addressByUid,
    flexStationIdByUid: maps.flexStationIdByUid,
    secondaryIdByUid: maps.secondaryIdByUid,
    numberOfSensorsByUid: maps.numberOfSensorsByUid,
    targetTempByUid: maps.targetTempByUid,
    currentTempByUid: maps.currentTempByUid,
    avgTemp24hByUid: maps.avgTemp24hByUid,
    energyMwhByUid: maps.energyMwhByUid,
    loading: {
      flex: flexStationsQueries.isLoading,
      temps: loadingTempAverages || isLoadingTargetTemp,
      energy: isLoadingEnergyData,
    },
  });

  const mapCenter = useMemo(
    () => calculateMapCenter(installAddresses) || undefined,
    [installAddresses]
  );

  logger.debug("RENDER Dashboard", { user });

  if (!loadingSubstations && (!substations || substations.length === 0)) {
    return (
      <Grid container direction="row" spacing={2} p={2}>
        <Grid item xs={12}>
          <SolidCard
            title={t("average_indoor_temperature", { ns: "propai" })}
            titleVariant="titleMedium"
          >
            <Box p={3} textAlign="center">
              <Typography variant="bodyMedium" color="text.secondary">
                {t("no_substations_available")}
              </Typography>
            </Box>
          </SolidCard>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container direction="row" spacing={2} p={4}>
      <Grid item xs={12}>
        <Grid
          container
          direction="row"
          alignItems="center"
          columnSpacing={2}
          justifyContent="stretch"
          sx={{ height: 300 }}
        >
          <Grid item xs={6} sx={{ height: "100%" }}>
            <SolidCard
              title={t("average_indoor_temperature", { ns: "propai" })}
              titleVariant="titleMedium"
              sx={{ height: "100%" }}
            >
              <PropertiesKpiCards
                data={kpiInput}
                labels={{
                  last7Days: t("propai:overview_last_7_days"),
                  last24Hours: t("propai:overview_last_24_hours"),
                }}
                loading={loadingTimeSeries}
              />
            </SolidCard>
          </Grid>
          <Grid item xs={6} sx={{ height: "100%" }}>
            <SolidCard title={t("locations")} titleVariant="titleMedium" sx={{ height: "100%" }}>
              <PropertiesMap
                addresses={installAddresses}
                mapCenter={mapCenter}
                mapId="org-map"
                mapKey={selectedOrganization?.uid}
              />
            </SolidCard>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <SolidCard
          title={t("dashboard_all_properties")}
          titleVariant="titleMedium"
          testId="dashboard-table"
        >
          <PropertiesTable
            rows={tableRows}
            labels={{
              address: t("_common:text_install_address"),
              flexStationId: t("flex_station_id"),
              secondaryId: t("substation_secondary_id"),
              numberOfSensors: t("dashboard_table_number_of_sensors"),
              targetTemp: t("propai:target_temperature"),
              indoorTemp: t("indoor_temp"),
              currentAverage: t("current_average"),
              averageLast24h: t("average_last_24h"),
              energyUse: t("energy_use"),
            }}
            energyYearLabel={`${t("text_for")} ${energyYear}`}
            initialColumnVisibility={{
              flexStationId: false,
              secondaryId: false,
            }}
            renderAddressCell={(row) => (
              <Typography variant="bodyMedium" component="span">
                <Link
                  to="/$organizationId/properties/$substationId/overview"
                  params={{
                    organizationId: selectedOrganization!.uid,
                    substationId: row.uid,
                  }}
                  style={{ textDecoration: "none", color: theme.palette.primary.main }}
                >
                  {row.address}
                </Link>
              </Typography>
            )}
          />
        </SolidCard>
      </Grid>
    </Grid>
  );
}
